// src/utils/index.ts - COMPLETE WITH IMAGE UTILITIES
export const formatCurrency = (amount: number): string => {
  if (isNaN(amount)) return '₦0';
  return `₦${amount.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

export const calculatePlatformFee = (totalPrice: number): number => {
  return Math.round(totalPrice * 0.1);
};

export const calculateRiderPayout = (totalPrice: number): number => {
  return Math.round(totalPrice * 0.9);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-NG', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatTimeAgo = (timestamp: string): string => {
  if (!timestamp) return 'Just now';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    
    if (isNaN(date.getTime())) {
      return 'Just now';
    }
    
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks === 1 ? '' : 's'} ago`;
    if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`;
    return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`;
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Just now';
  }
};

export const formatTime = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-NG', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '';
  }
};

// ============ IMAGE UTILITIES - CRITICAL FOR PRODUCTION ============

/**
 * Get the API URL from environment variables
 * This ensures images work in both development and production
 */
export const getApiUrl = (): string => {
  // Check for VITE_API_URL first (production)
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Development fallback
  if (import.meta.env.DEV) {
    return 'http://localhost:5000';
  }
  // Production fallback
  return 'https://api.carryngo.ng';
};

/**
 * Get the full URL for an image
 * Converts relative paths like "/uploads/profiles/image.jpg" to full URLs
 * 
 * @param path - The image path (can be relative or absolute)
 * @returns Full URL or undefined if path is empty
 */
export const getImageUrl = (path: string | undefined | null): string | undefined => {
  if (!path) return undefined;
  
  const trimmedPath = path.trim();
  if (!trimmedPath) return undefined;
  
  // If it's already a full URL with http/https, return as is
  if (trimmedPath.startsWith('http://') || trimmedPath.startsWith('https://')) {
    return trimmedPath;
  }
  
  // If it's a data URL (base64 encoded image), return as is
  if (trimmedPath.startsWith('data:image/')) {
    return trimmedPath;
  }
  
  // If it's a relative path starting with /uploads/, prepend API URL
  if (trimmedPath.startsWith('/uploads/')) {
    const apiUrl = getApiUrl();
    return `${apiUrl}${trimmedPath}`;
  }
  
  // If it's a relative path without leading slash, add it
  if (trimmedPath.startsWith('uploads/')) {
    const apiUrl = getApiUrl();
    return `${apiUrl}/${trimmedPath}`;
  }
  
  // If it's just a filename, assume it's in profiles directory
  if (!trimmedPath.includes('/')) {
    const apiUrl = getApiUrl();
    return `${apiUrl}/uploads/profiles/${trimmedPath}`;
  }
  
  // For any other relative path, prepend API URL
  const apiUrl = getApiUrl();
  return `${apiUrl}/${trimmedPath}`;
};

/**
 * Get placeholder image based on type
 */
export const getPlaceholderImage = (type: 'profile' | 'cover' | 'bike' | 'document'): string => {
  const placeholders = {
    profile: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpath d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"%3E%3C/path%3E%3Ccircle cx="12" cy="7" r="4"%3E%3C/circle%3E%3C/svg%3E',
    cover: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2"%3E%3Crect x="3" y="3" width="18" height="18" rx="2"%3E%3C/rect%3E%3C/svg%3E',
    bike: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2"%3E%3Ccircle cx="5.5" cy="17.5" r="2.5"%3E%3C/circle%3E%3Ccircle cx="18.5" cy="17.5" r="2.5"%3E%3C/circle%3E%3Cpath d="M15 6a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V6z"%3E%3C/path%3E%3C/svg%3E',
    document: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 24 24" fill="none" stroke="%239ca3af" stroke-width="2"%3E%3Cpath d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"%3E%3C/path%3E%3Cpolyline points="14 2 14 8 20 8"%3E%3C/polyline%3E%3C/svg%3E'
  };
  return placeholders[type] || placeholders.profile;
};

/**
 * Handle image load error - replaces broken image with placeholder
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>, 
  type: 'profile' | 'cover' | 'bike' | 'document' = 'profile'
): void => {
  const img = event.currentTarget;
  img.src = getPlaceholderImage(type);
  img.onerror = null; // Prevent infinite loop
};

/**
 * Check if an image exists on the server
 */
export const checkImageExists = async (url: string): Promise<boolean> => {
  if (!url) return false;
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get user avatar URL with fallback
 */
export const getUserAvatar = (profileImage?: string, fullName?: string): string => {
  if (profileImage) {
    const fullUrl = getImageUrl(profileImage);
    if (fullUrl) return fullUrl;
  }
  // Fallback to initials avatar
  const initials = getInitials(fullName || 'User');
  return `https://ui-avatars.com/api/?name=${initials}&background=10B981&color=fff&bold=true&size=100`;
};

// ============ END IMAGE UTILITIES ============

// ============ TIMER FUNCTIONS ============

export const formatCountdown = (seconds: number): string => {
  if (seconds <= 0) return '00:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const getTimeRemaining = (deadline: string): string => {
  if (!deadline) return '--:--';
  
  try {
    const end = new Date(deadline).getTime();
    const now = Date.now();
    const diffMs = end - now;
    
    if (diffMs <= 0) return '00:00';
    
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } catch (error) {
    return '--:--';
  }
};

export const getMinutesRemaining = (deadline: string): number => {
  if (!deadline) return 0;
  
  try {
    const end = new Date(deadline).getTime();
    const now = Date.now();
    const diffMs = end - now;
    
    if (diffMs <= 0) return 0;
    return Math.floor(diffMs / 60000);
  } catch (error) {
    return 0;
  }
};

export const getSecondsRemaining = (deadline: string): number => {
  if (!deadline) return 0;
  
  try {
    const end = new Date(deadline).getTime();
    const now = Date.now();
    const diffMs = end - now;
    
    if (diffMs <= 0) return 0;
    return Math.floor(diffMs / 1000);
  } catch (error) {
    return 0;
  }
};

export const isDeadlinePassed = (deadline: string): boolean => {
  if (!deadline) return false;
  
  try {
    const end = new Date(deadline).getTime();
    return Date.now() > end;
  } catch (error) {
    return false;
  }
};

export const calculateAcceptanceDeadline = (createdAt: string): string => {
  try {
    const created = new Date(createdAt).getTime();
    const deadline = new Date(created + 15 * 60 * 1000);
    return deadline.toISOString();
  } catch (error) {
    return new Date(Date.now() + 15 * 60 * 1000).toISOString();
  }
};

export const getTimerStatus = (deadline: string): { 
  color: string; 
  text: string; 
  isUrgent: boolean;
  minutesLeft: number;
} => {
  if (!deadline) {
    return { color: 'text-gray-500', text: '--:--', isUrgent: false, minutesLeft: 0 };
  }
  
  try {
    const end = new Date(deadline).getTime();
    const now = Date.now();
    const diffMs = end - now;
    
    if (diffMs <= 0) {
      return { color: 'text-red-600', text: 'Expired', isUrgent: true, minutesLeft: 0 };
    }
    
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    const timeString = `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    
    if (mins < 5) {
      return { color: 'text-red-600 font-bold animate-pulse', text: timeString, isUrgent: true, minutesLeft: mins };
    } else if (mins < 10) {
      return { color: 'text-orange-600', text: timeString, isUrgent: false, minutesLeft: mins };
    } else {
      return { color: 'text-emerald-600', text: timeString, isUrgent: false, minutesLeft: mins };
    }
  } catch (error) {
    return { color: 'text-gray-500', text: '--:--', isUrgent: false, minutesLeft: 0 };
  }
};

// ============ END TIMER FUNCTIONS ============

export const validatePhoneNumber = (phone: string): boolean => {
  const phoneRegex = /^(080|081|070|090|091)\d{8}$/;
  return phoneRegex.test(phone.replace(/\s+/g, ''));
};

export const getAvatarUrl = (name: string, color?: string): string => {
  const initials = getInitials(name);
  const bgColor = color || '10B981';
  return `https://ui-avatars.com/api/?name=${initials}&background=${bgColor}&color=fff&bold=true&size=100`;
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    pending_payment: 'bg-yellow-100 text-yellow-800',
    payment_confirmed: 'bg-blue-100 text-blue-800',
    accepted: 'bg-blue-100 text-blue-800',
    started: 'bg-indigo-100 text-indigo-800',
    in_progress: 'bg-indigo-100 text-indigo-800',
    awaiting_offline_payment: 'bg-orange-100 text-orange-800',
    awaiting_rider_confirmation: 'bg-purple-100 text-purple-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    expired: 'bg-gray-100 text-gray-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getServiceIcon = (serviceType: string): string => {
  return serviceType === 'ride' ? '🛵' : '📦';
};

export const calculateCommitmentFee = (totalPrice: number): number => {
  return Math.round(totalPrice * 0.1);
};

export const timeAgo = (dateString: string): string => {
  return formatTimeAgo(dateString);
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getRatingColor = (rating: number): string => {
  if (rating >= 4.5) return 'text-green-600';
  if (rating >= 4.0) return 'text-yellow-500';
  if (rating >= 3.0) return 'text-orange-500';
  return 'text-red-500';
};

export const formatDistance = (distance?: number): string => {
  if (!distance) return 'N/A';
  return `${distance.toFixed(1)} km`;
};

export const getPaymentStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-blue-100 text-blue-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const generateOrderNumber = (): string => {
  const prefix = 'TEMP';
  const timestamp = Date.now().toString().slice(-8);
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `${prefix}-${timestamp}-${random}`;
};

export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371;
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const d = R * c;
  return Math.round(d * 10) / 10;
};

const deg2rad = (deg: number): number => {
  return deg * (Math.PI/180);
};

export const maskPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  if (phone.length < 10) return phone;
  const last4 = phone.slice(-4);
  const masked = '*'.repeat(phone.length - 4);
  return masked + last4;
};

export const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return email;
  const [local, domain] = email.split('@');
  if (local.length < 3) return email;
  const maskedLocal = local.slice(0, 2) + '*'.repeat(local.length - 2);
  return `${maskedLocal}@${domain}`;
};

export const getInitials = (name: string): string => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const downloadFile = (url: string, filename: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getBrowserInfo = (): string => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  
  if (ua.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (ua.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (ua.indexOf('Safari') > -1) browser = 'Safari';
  else if (ua.indexOf('Edge') > -1) browser = 'Edge';
  else if (ua.indexOf('MSIE') > -1 || ua.indexOf('Trident/') > -1) browser = 'Internet Explorer';
  
  return browser;
};

export const getOSInfo = (): string => {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  
  if (ua.indexOf('Windows') > -1) os = 'Windows';
  else if (ua.indexOf('Mac') > -1) os = 'macOS';
  else if (ua.indexOf('Linux') > -1) os = 'Linux';
  else if (ua.indexOf('Android') > -1) os = 'Android';
  else if (ua.indexOf('iOS') > -1) os = 'iOS';
  
  return os;
};

// ============ ORDER MANAGEMENT UTILITIES ============
export const getOrderStatusDisplay = (status: string): { label: string; color: string; icon: string } => {
  const map: Record<string, { label: string; color: string; icon: string }> = {
    pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
    payment_confirmed: { label: 'Payment Confirmed', color: 'bg-green-100 text-green-800', icon: '💰' },
    accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-800', icon: '✅' },
    in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-800', icon: '🚴' },
    awaiting_offline_payment: { label: 'Awaiting Offline Payment', color: 'bg-orange-100 text-orange-800', icon: '💵' },
    awaiting_rider_confirmation: { label: 'Awaiting Confirmation', color: 'bg-indigo-100 text-indigo-800', icon: '⏰' },
    completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: '🎉' },
    cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: '❌' },
    expired: { label: 'Expired', color: 'bg-gray-100 text-gray-800', icon: '⌛' }
  };
  return map[status] || { label: status, color: 'bg-gray-100 text-gray-800', icon: '📦' };
};

export const getUrgencyLevel = (minutesLeft: number): { level: 'critical' | 'warning' | 'normal'; color: string } => {
  if (minutesLeft < 5) return { level: 'critical', color: 'text-red-600 bg-red-50 border-red-200' };
  if (minutesLeft < 10) return { level: 'warning', color: 'text-orange-600 bg-orange-50 border-orange-200' };
  return { level: 'normal', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
};

export const isOrderExpiring = (createdAt: string): boolean => {
  const created = new Date(createdAt).getTime();
  const deadline = created + (15 * 60 * 1000);
  return Date.now() > deadline;
};

export const getTimeUntilExpiry = (createdAt: string): number => {
  const created = new Date(createdAt).getTime();
  const deadline = created + (15 * 60 * 1000);
  return Math.max(0, Math.floor((deadline - Date.now()) / 1000));
};